import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import BottomSheetModal from "./BottomSheetModal";
import { Divider } from "./SheetPrimitives";
import CustomInput from "../AuthenticationComponents/CustomInput";
import SecondaryButton from "./SecondaryButton";

import tickIcon from "../../assets/images/support/tick-circle-white.svg";
import cancelIcon from "../../assets/images/user-dashboard/remove-circle.svg";

// Stripe
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * TopUpModal — modal for topping up Stripe connected account balance
 * Built on the shared BottomSheetModal shell with Stripe Elements integration
 * ---------------------------------------------------------------------- */

const TopUpModal = ({
  isOpen,
  onClose,
  vendorListingId,
  onSuccess,
  onPaymentFailure,
}) => {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [cardError, setCardError] = useState("");
  const topUpInFlightRef = useRef(false);
  const stripe = useStripe();
  const elements = useElements();

  // Configure toastr options
 useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  const handleTopUp = async () => {
    if (topUpInFlightRef.current) return;

    if (!amount || parseFloat(amount) <= 0) {
      setCardError("Please enter a valid amount");
      return;
    }

    if (!stripe || !elements) {
      setCardError("Stripe is not loaded. Please refresh the page.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardError("Card element is not ready. Please try again.");
      return;
    }

    topUpInFlightRef.current = true;
    setLoading(true);
    setCardError("");
    try {
      const payload = {
        amount: parseFloat(amount),
        currency: "usd",
        description: "Restaurant Connect funding",
        vendor_listing_id: vendorListingId,
      };

      const response = await post(endpoints.restaurantStripeConnectTopups, payload);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        if (data.payment && data.payment.client_secret) {
          // Use Stripe Elements to confirm card payment
          const { error, paymentIntent } = await stripe.confirmCardPayment(data.payment.client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: 'Restaurant',
              },
            },
          });

          if (error) {
            const message = error.message || "Payment confirmation failed.";
            setCardError(message);
            toastr.error(message);
            onPaymentFailure({
              fundingId: data.id || data.funding_id || data.account_funding_id || data.topup_id || null,
            });
          } else if (paymentIntent && ['succeeded', 'processing'].includes(paymentIntent.status)) {
            toastr.success("Top-up successful!");
            setAmount("");
            setCardError("");
            onClose();
            if (onSuccess) {
              onSuccess({
                fundingId: data.id || data.funding_id || data.account_funding_id || data.topup_id || null,
                paymentIntentId: paymentIntent.id || data.payment_intent_id || data.payment?.payment_intent_id || null,
                paymentIntentStatus: paymentIntent.status,
              });
            }
          } else {
            const message = "Payment confirmation is incomplete. Please try again.";
            setCardError(message);
            toastr.error(message);
          }
        } else {
          setCardError("Failed to initiate payment. No client secret received.");
          toastr.error("Failed to initiate payment. No client secret received.");
        }
      } else {
        setCardError("Failed to initiate top-up");
        toastr.error("Failed to initiate top-up");
      }
    } catch (err) {
      console.error("Failed to top-up:", err);
      const errorMessage = extractErrorMessage(err);
      setCardError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      topUpInFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="topup-title">
      <div className="topup-modal-wrap">
        <h2 className="mc-title mc-title--center" id="topup-title" style={{ marginTop: "var(--gap-lg)" }}>
          Top Up Balance
        </h2>
        <p className="mc-description mc-description--center">
          Add funds to your Stripe connected account securely.
        </p>

        <Divider />

        <div className="topup-form">
          <CustomInput
            label="Amount (USD)"
            name="amount"
            type="number"
            value={amount}
            placeholder="100.00"
            onChange={(e) => setAmount(e.target.value)}
            className="white-bg"
            min="0.01"
            step="0.01"
          />
          
          <div className="topup-card-input">
            <label className="topup-form-label">Card Information</label>
            <div className="topup-card-element">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
                onChange={(event) => {
                  setCardError(event.error ? event.error.message : "");
                }}
              />
            </div>
            {cardError && <p className="topup-error">{cardError}</p>}
          </div>
        </div>

        <Divider />

        <div className="topup-actions">
          <SecondaryButton 
            label="Cancel" 
            variant="light-grey" 
            icon={cancelIcon}
            onClick={() => {
              setAmount("");
              setCardError("");
              onClose();
            }}
            disabled={loading}
          />
          <SecondaryButton 
            label="Confirm Top Up"
            variant="orange" 
            icon={tickIcon}
            onClick={handleTopUp} 
            disabled={loading || !amount || !!cardError}
            loading={loading}
          />
        </div>
      </div>
    </BottomSheetModal>
  );
};

TopUpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vendorListingId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  onPaymentFailure: PropTypes.func,
};

TopUpModal.defaultProps = {
  onSuccess: () => {},
  onPaymentFailure: () => {},
};

export default TopUpModal;
